import { Controller, useFormContext } from "react-hook-form";

import type { SchemaField, SchemaGroup } from "@motolynk/api-client";
import { numberProblem, useRules, type NumberRule } from "@motolynk/app-core";
import { useTranslation } from "@motolynk/i18n";
import {
  Checkbox,
  DatePicker,
  FormField,
  Input,
  Label,
  NumberInput,
  Select,
  Textarea,
} from "@motolynk/ui";

import type { EditorValues } from "./values";

const NONE = "__none__";
/** Values are stored as Decimal(18,4) (backend products AttributeValue.value_decimal): 14 digits before the point. */
const LARGEST = 99_999_999_999_999;

/** A number rule from the attribute's validation (catalog/services/attributes.py: min, max, decimal_places). */
function specNumberRule(field: SchemaField): NumberRule {
  const rules = field.validation ?? {};
  const read = (key: string) => {
    const value = rules[key];
    if (value === undefined || value === null || value === "") return undefined;
    const number = Number(value);
    return Number.isFinite(number) ? number : undefined;
  };
  const places = read("decimal_places");
  return {
    decimals: field.input_type === "integer" ? 0 : (places ?? 4),
    min: read("min"),
    max: read("max") ?? LARGEST,
  };
}

/** The category's specification fields (variant axes are handled by the variants section). */
export function SpecsSection({ groups }: { groups: SchemaGroup[] }) {
  const { t } = useTranslation("products");
  const visible = groups
    .map((group) => ({ ...group, fields: group.fields.filter((field) => !field.is_variant_axis) }))
    .filter((group) => group.fields.length > 0);
  if (visible.length === 0)
    return <p className="text-sm text-muted-foreground">{t("editor.noSpecs")}</p>;
  return (
    <div className="space-y-6">
      {visible.map((group) => (
        <fieldset key={group.code} className="space-y-4">
          <legend className="mb-2 text-sm font-semibold">{group.name}</legend>
          <div className="grid gap-4 sm:grid-cols-2">
            {group.fields.map((field) => (
              <SpecField key={field.code} field={field} />
            ))}
          </div>
        </fieldset>
      ))}
    </div>
  );
}

function SpecField({ field }: { field: SchemaField }) {
  const { t } = useTranslation("products");
  const { control, formState } = useFormContext<EditorValues>();
  const rules = useRules();
  const isNumber = field.input_type === "integer" || field.input_type === "decimal";
  const numberRule = isNumber ? specNumberRule(field) : null;
  const name = `attributes.${field.code}` as const;
  const error = (formState.errors.attributes as Record<string, { message?: string }> | undefined)?.[
    field.code
  ]?.message;
  const label = field.unit ? `${field.label} (${field.unit})` : field.label;
  const hint = field.help_text || undefined;

  if (field.input_type === "multiselect") {
    return (
      <FormField
        label={label}
        hint={hint}
        error={error}
        required={field.required}
        className="sm:col-span-2"
      >
        <Controller
          control={control}
          name={name}
          render={({ field: input }) => {
            const chosen = Array.isArray(input.value) ? (input.value as string[]) : [];
            return (
              <div className="flex flex-wrap gap-x-5 gap-y-2" role="group" aria-label={field.label}>
                {field.options.map((option) => {
                  const id = `spec-${field.code}-${option.value}`;
                  return (
                    <div key={option.value} className="flex items-center gap-2">
                      <Checkbox
                        id={id}
                        checked={chosen.includes(option.value)}
                        onCheckedChange={(on) =>
                          input.onChange(
                            on === true
                              ? [...chosen, option.value]
                              : chosen.filter((v) => v !== option.value),
                          )
                        }
                      />
                      <Label htmlFor={id} className="font-normal">
                        {option.label}
                      </Label>
                    </div>
                  );
                })}
              </div>
            );
          }}
        />
      </FormField>
    );
  }

  return (
    <FormField
      label={label}
      hint={hint}
      error={error}
      required={field.required}
      className={field.input_type === "textarea" ? "sm:col-span-2" : undefined}
    >
      <Controller
        control={control}
        name={name}
        rules={
          numberRule
            ? {
                validate: (value) => rules.message(numberProblem(value, numberRule)) ?? true,
              }
            : undefined
        }
        render={({ field: input }) => {
          const value = input.value;
          switch (field.input_type) {
            case "select":
              return (
                <Select
                  value={typeof value === "string" && value ? value : NONE}
                  onValueChange={(next) => input.onChange(next === NONE ? "" : next)}
                  options={[
                    { value: NONE, label: "—" },
                    ...field.options.map((option) => ({
                      value: option.value,
                      label: option.label,
                    })),
                  ]}
                />
              );
            case "boolean":
              return (
                <Select
                  value={value === true ? "yes" : value === false ? "no" : NONE}
                  onValueChange={(next) => input.onChange(next === NONE ? "" : next === "yes")}
                  options={[
                    { value: NONE, label: "—" },
                    { value: "yes", label: t("editor.yes") },
                    { value: "no", label: t("editor.no") },
                  ]}
                />
              );
            case "textarea":
              return (
                <Textarea
                  rows={3}
                  value={(value as string) ?? ""}
                  onChange={input.onChange}
                  onBlur={input.onBlur}
                />
              );
            case "date":
              return (
                <DatePicker
                  value={(value as string) ?? ""}
                  onChange={input.onChange}
                  onBlur={input.onBlur}
                />
              );
            case "integer":
            case "decimal":
              return (
                <NumberInput
                  decimals={numberRule?.decimals ?? 0}
                  min={numberRule?.min}
                  max={numberRule?.max}
                  allowNegative={numberRule?.min === undefined || numberRule.min < 0}
                  aria-label={label}
                  aria-invalid={Boolean(error)}
                  value={value === undefined || value === null ? "" : String(value)}
                  onChange={input.onChange}
                  onBlur={input.onBlur}
                />
              );
            default:
              return (
                <Input
                  value={(value as string) ?? ""}
                  onChange={input.onChange}
                  onBlur={input.onBlur}
                />
              );
          }
        }}
      />
    </FormField>
  );
}
